import type { Document } from "@/types/documents";

// Core folder configuration types
export interface FolderConfig {
  category?: "documents" | "drawings";
  template_type?: string;
  auto_created?: boolean;
  [key: string]: unknown;
}

/** Folders drawn differently from an ordinary one. */
export type FolderVariant = "brief" | "issue-attachments";

export interface TreeFolder {
  id: string;
  name: string; // Derived from folder_path
  parentId: string | null;
  parent_document_id?: string; // Folder can be child of a document
  category: "documents" | "drawings"; // Derived from folder_config
  isCustom?: boolean;
  building_id: number;
  project_id?: number;
  children: TreeFolder[];
  documentCount?: number;
  // Database fields
  folder_path?: string;
  folder_depth?: number;
  is_project_scoped?: boolean;
  folder_config?: FolderConfig;
  // Virtual container fields (for ZIP/archive entries in folder tree)
  isContainer?: boolean;
  containerId?: string; // Real document ID for fetching children
  /**
   * "brief": the project's Brief folder — its own icon, and it cannot be renamed,
   * moved or deleted. "issue-attachments": the system folder issue uploads land
   * in, drawn with the Issues icon.
   */
  variant?: FolderVariant;
  /** Shown in place of `name` (these folders' names follow the UI language). */
  displayName?: string;
}

export interface DocumentFolder {
  id: string;
  parent_id: string | null;
  is_custom: boolean;
  building_id: number;
  project_id?: number;
  created_by: number;
  created_at: string;
  modified_by?: number;
  modified_at?: string;
  org_id: string;
  // Database fields
  folder_path?: string;
  folder_depth?: number;
  is_project_scoped?: boolean;
  display_order?: number;
  folder_config?: FolderConfig;
  // Derived fields
  name?: string; // Derived from folder_path
  category?: "documents" | "drawings"; // Derived from folder_config
}

// Utility functions for folder operations
export const extractFolderName = (folderPath: string): string => {
  if (!folderPath) return "";
  const parts = folderPath.split("/").filter(Boolean);
  return parts[parts.length - 1] || "";
};

export const extractCategory = (
  folderConfig: FolderConfig | undefined
): "documents" | "drawings" => {
  return (folderConfig?.category as "documents" | "drawings") || "documents";
};

export const buildFolderPath = (name: string, parentPath?: string): string => {
  if (!parentPath || parentPath === "/") {
    return `/${name}`;
  }
  return `${parentPath}/${name}`;
};

// Search result types
export interface DocumentSearchResult {
  query: string;
  documents: Document[];
}

// Error handling types
export interface DocumentError {
  code?: string;
  message: string;
  details?: unknown;
}

// Data extraction types
export interface DocumentDataExtraction {
  text?: string;
  metadata?: Record<string, unknown>;
  extractedFields?: Record<string, unknown>;
}

// Supabase row types with joined data
export interface DocumentRowWithProfile {
  id: number;
  filename: string;
  file_path: string;
  building_id: number;
  content_type: string;
  size: number;
  created_at: string;
  created_by: number;
  org_id: string;
  category?: string;
  subcategory?: string;
  subcategories?: string[];
  version?: number;
  version_group?: string;
  modified_by?: number;
  modified_at?: string;
  project_id?: number;
  original_created_at?: string;
  folder_id?: number;
  is_deleted?: boolean;
  deleted_at?: string;
  deleted_by?: number;
  user_profiles?: {
    first_name: string;
    last_name: string;
  };
  deleted_by_profile?: {
    first_name: string;
    last_name: string;
  };
}

// Real-time subscription payload types
export interface DocumentRealtimePayload {
  eventType: "INSERT" | "UPDATE" | "DELETE";
  new?: DocumentRowWithProfile;
  old?: DocumentRowWithProfile;
}

// Folder operation types
export interface FolderInsertData {
  building_id: number;
  folder_path: string;
  created_by: number;
  org_id: string;
  is_custom: boolean;
  folder_config: FolderConfig;
  project_id?: number;
  parent_id?: number;
  is_project_scoped?: boolean;
}

export interface FolderUpdateData {
  modified_by: number;
  modified_at: string;
  folder_path?: string;
  parent_id?: number | null;
}

// Component prop interfaces
export interface ManualSortingProps {
  buildingId: string;
  documents: Document[];
  onMoveDocuments: (
    documents: Document[],
    targetFolder: string,
    targetCategory: string
  ) => Promise<boolean | void>;
  folders: TreeFolder[];
  onRefresh?: () => Promise<void>;
  onCategorizeDocument: (document: Document) => void;
}

export interface DocumentUploadProps {
  buildingId: string;
  onUploadComplete?: () => void;
  category?: "documents" | "drawings";
  folderId?: string;
  onDataExtracted?: (data: DocumentDataExtraction) => void;
}

export interface FolderNavigationProps {
  buildingId: string;
  currentView: string;
  onViewChange: (view: string) => void;
  onSearch: (query: string) => void;
  onUploadClick: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  chatSearchResults: DocumentSearchResult | null;
}

export interface DocumentsMainViewProps {
  buildingId: string;
  chatSearchResults: DocumentSearchResult | null;
}

export interface BuildingDocumentsProps {
  buildingId: string;
  documents: Document[];
}

// Error callback types
export interface ErrorCallback {
  (error: DocumentError): void;
}

// Folder stats
export interface FolderStats {
  documentCount: number;
  folderCount: number;
}

// Database operation results
export interface EmptyTrashResult {
  success: boolean;
  count: number;
}

export interface AutoSortResult {
  success: boolean;
  count: number;
}
